/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import currencyFormatter from 'utils/currencyFormatter';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getPercepcionesLimiteMaximo,
  postPercepcionLimiteMaximo,
  patchPercepcionLimiteMaximo,
  deletePercepcionLimiteMaximo,
  getPercepcionLimiteMaximo,
} from 'api/catalogos/percepcionesLimiteMaximo';
import { ESTATUS_ACTIVO } from 'utils/constants';
import moment from 'moment';

const PercepcionesLimiteMaximo = ({ percepcionId, permission, disabled }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [disableFields, setDisableFields] = useState(false);
  const formInstances = [form];

  // Función para cargar los datos de la tabla (filtrados por percepcionId)
  const fetchData = async () => {
    if (!percepcionId) return;
    setLoading(true);
    try {
      const _data = await getPercepcionesLimiteMaximo({ percepcion: percepcionId });
      setData(formatReceived(_data));
    } catch (err) {
      onError(err);
    }
    setLoading(false);
  };

  // Carga inicial de catálogos y datos
  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      try {
        const _estados = await getEstadosGlobales();

        if (mounted) {
          setEstados(_estados);

          if (percepcionId) {
            await fetchData();
          }
        }
      } catch (err) {
        onError(err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchAll();

    return () => { mounted = false; };
  }, [percepcionId]);

  const onCancel = () => {
    setVisible(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    try {
      const [key] = selectedRowKeys;
      const match = responseData || data.find((item) => item.id === key);
      const _record = await getPercepcionLimiteMaximo(match.id);

      if (_record) {
        form.resetFields();
        const formatted = formatReceived(_record);

        if (formatted.vigencia_inicial) {
          formatted.vigencia_inicial = moment(formatted.vigencia_inicial);
        }
        if (formatted.vigencia_final) formatted.vigencia_final = moment(formatted.vigencia_final);

        form.setFieldsValue(formatted);
        setDisableFields(_record?.estados_globales !== ESTATUS_ACTIVO);
        setVisible(true);
      }
    } catch (err) {
      onError(err);
    }
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      // Aseguramos enviar el ID de la percepción padre
      values.percepcion = percepcionId;

      // Formatear fechas a string YYYY-MM-DD
      if (values.vigencia_inicial) values.vigencia_inicial = values.vigencia_inicial.format('YYYY-MM-DD');
      if (values.vigencia_final) values.vigencia_final = values.vigencia_final.format('YYYY-MM-DD');

      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchPercepcionLimiteMaximo(key, values)
        : postPercepcionLimiteMaximo(values));

      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deletePercepcionLimiteMaximo(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    form.setFieldsValue({
      estados_globales: ESTATUS_ACTIVO,
    });
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      width: 150,
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Vigencia Inicial',
      dataIndex: 'vigencia_inicial',
      key: 'vigencia_inicial',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = { required: true, message: 'El campo es requerido' };

  return (
    <Row style={{ marginTop: 20 }}>
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          titleTable="Importe Límite Máximo"
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          permission={permission}
          disabled={disabled || !percepcionId}
          allowExportPDF={false}
        />

        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          width={800}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Importe Límite Máximo`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="formLimiteMaximo"
            initialValues={{ estados_globales: ESTATUS_ACTIVO }}
          >
            <Row gutter={16}>
              {/* Campo IMPORTE */}
              <Col xs={24} md={12}>
                <Form.Item
                  name="importe"
                  label="Importe"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <Input
                    prefix="$"
                    type="number"
                    step="0.01"
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>

              {/* Campo VIGENCIA INICIAL */}
              <Col xs={24} md={12}>
                <Form.Item
                  name="vigencia_inicial"
                  label="Vigencia Inicial"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>

              {/* Campo VIGENCIA FINAL */}
              <Col xs={24} md={12}>
                <Form.Item
                  name="vigencia_final"
                  label="Vigencia Final"
                  hasFeedback
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>

              {/* Campo ESTATUS */}
              <Col xs={24} md={12}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <Select
                    dataSource={estados}
                    disabled={selectedRowKeys.length <= 0 || disableFields}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="¿Estás seguro de eliminar este registro de Importe Límite Máximo?"
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default PercepcionesLimiteMaximo;
